import React, { useEffect, useState, useMemo } from "react";
import { Dashboard } from "./components/dashboard/Dashboard";
import { NavigationMenu } from "./components/navigation-menu/NavigationMenu";
import { DailyPlanner } from "./components/daily-planner/DailyPlanner";
import { generateId } from "./utils/Utils";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SharedCollectionView from "./pages/shared/SharedCollectionView";
import { initializeFolders, findCollectionById, getFirstCollectionId, ensureInboxCollection, sanitizeFolders } from "./utils/folderUtils";
import { saveToStorage, loadFromStorage, removeFromStorage, STORAGE_KEYS } from "./utils/storageUtils";
import { Folder, Collection, Element, TrashItem, Task } from "./types";
import { useReminders } from "./hooks/useReminders";
import { PlannerTaskInfo } from "./components/daily-planner/types";
import { SecretPinModal } from './components/secret-collections/SecretPinModal';
import { ConfirmationDialog } from './components/confirmation-dialog/ConfirmationDialog';
import { useSecretCollections } from './hooks/useSecretCollections';
import { usePwaInstallPrompt } from './hooks/usePwaInstallPrompt';
import { AppBackup } from './utils/backupUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faCircleInfo, faDownload, faXmark } from '@fortawesome/free-solid-svg-icons';

function App() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [folders, setFolders] = useState<Folder[]>(initializeFolders);
    const [isBetaNoticeDismissed, setIsBetaNoticeDismissed] = useState(false);
    const [isBetaInfoOpen, setIsBetaInfoOpen] = useState(false);
    const [isInstallBannerDismissed, setIsInstallBannerDismissed] = useState(false);
    const secretCollections = useSecretCollections({ setFolders });
    const { isInstallAvailable, isIosInstallAvailable, requestInstall } = usePwaInstallPrompt();
    const shouldShowInstallBanner = (
        (isInstallAvailable || isIosInstallAvailable)
        && !isInstallBannerDismissed
    );
    const shouldShowIosInstallGuide = isIosInstallAvailable && !isInstallAvailable;

    useEffect(() => {
        document.body.classList.toggle('beta-notice-dismissed', isBetaNoticeDismissed);
        return () => document.body.classList.remove('beta-notice-dismissed');
    }, [isBetaNoticeDismissed]);

    useEffect(() => {
        document.body.classList.toggle('install-banner-visible', shouldShowInstallBanner);
        return () => document.body.classList.remove('install-banner-visible');
    }, [shouldShowInstallBanner]);

    useEffect(() => {
        const hasInbox = folders.some((folder) =>
            (folder.collections || []).some((collection) => collection.name === 'Inbox')
        );

        if (!hasInbox) {
            setFolders((currentFolders) => ensureInboxCollection(currentFolders));
        }
    }, [folders]);

    // Load selectedCollectionId from localStorage or initialize with null
    const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(() => {
        const savedSelectedCollectionId = loadFromStorage<string>(STORAGE_KEYS.SELECTED_COLLECTION_ID);
        const savedSelectedListId = loadFromStorage<string>(STORAGE_KEYS.LEGACY_SELECTED_LIST_ID); // Fallback for migration
        
        return savedSelectedCollectionId || savedSelectedListId || null;
    });

    // Validate selectedCollectionId exists in folders, clear if stale
    useEffect(() => {
        if (selectedCollectionId) {
            const collectionExists = findCollectionById(selectedCollectionId, folders);
            if (!collectionExists) {
                removeFromStorage(STORAGE_KEYS.SELECTED_COLLECTION_ID);
                setSelectedCollectionId(null);
            }
        }
    }, [folders, selectedCollectionId]);

    const handleAddFolder = (folderName: string) => {
        if (!folderName.trim()) return;
        if (folderName.trim().toLowerCase() === 'general') {
            alert(t('folderMenu.reservedFolderName'));
            return;
        }

        const newFolder: Folder = {
            id: generateId(),
            name: folderName.trim(),
            collections: [],
            collapsed: false,
        };

        setFolders((prevFolders) => [
            ...prevFolders.map((folder) =>
                folder.name === 'General'
                    ? folder
                    : { ...folder, collapsed: true },
            ),
            newFolder,
        ]);
    };


    const handleMoveCollectionToFolder = (collectionId: string, folderId: string) => {
        const collectionToMove = findCollectionById(collectionId, folders);
        if (!collectionToMove) return;

        setFolders((prevFolders) =>
            prevFolders.map((folder) => {
                const updatedCollections = (folder.collections || []).filter((collection) => collection.id !== collectionId);
                
                if (folder.id === folderId) {
                    return { ...folder, collections: [...updatedCollections, collectionToMove] };
                }
                
                return { ...folder, collections: updatedCollections };
            }),
        );
    };

    const findCollectionAndFolder = (collectionId: string) => {
        for (const folder of folders) {
            const collection = (folder.collections || []).find((c) => c.id === collectionId);
            if (collection) {
                return { collection, folder };
            }
        }
        return { collection: null, folder: null };
    };

    const handleDeleteCollection = (collectionId: string) => {
        const { collection: collectionToDelete, folder: sourceFolder } = findCollectionAndFolder(collectionId);
        if (collectionToDelete?.name.trim().toLowerCase() === 'inbox') return;

        if (collectionToDelete && sourceFolder) {
            const trash = loadFromStorage<TrashItem[]>(STORAGE_KEYS.TRASH, []);
            if (trash) {
                const trashItem: TrashItem = {
                    id: generateId(),
                    type: 'collection',
                    collection: collectionToDelete,
                    deletedAt: new Date().toISOString(),
                    sourceFolderId: sourceFolder.id,
                    sourceFolderName: sourceFolder.name,
                };
                trash.push(trashItem);

                const updatedTrash = trash.filter(
                    item => !(item.type === 'element' && item.sourceCollectionId === collectionId)
                );
                saveToStorage(STORAGE_KEYS.TRASH, updatedTrash);
            }
        }

        setFolders((prevFolders) =>
            prevFolders.map((folder) => ({
                ...folder,
                collections: (folder.collections || []).filter((collection) => collection.id !== collectionId),
            })),
        );

        if (selectedCollectionId === collectionId) setSelectedCollectionId(null);
    };

    const handleDeleteFolder = (folderId: string) => {
        setFolders((prevFolders) => {
            const folderToDelete = prevFolders.find(f => f.id === folderId);
            if (!folderToDelete) return prevFolders;

            const deletedCollectionIds = (folderToDelete.collections || []).map(c => c.id);

            const trash = loadFromStorage<TrashItem[]>(STORAGE_KEYS.TRASH, []);
            if (trash) {
                const folderTrashItem: TrashItem = {
                    id: generateId(),
                    type: 'folder',
                    folder: folderToDelete,
                    deletedAt: new Date().toISOString(),
                };
                trash.push(folderTrashItem);

                const updatedTrash = trash.filter(
                    item => !(
                        (item.type === 'collection' && deletedCollectionIds.includes(item.sourceFolderId || '')) ||
                        (item.type === 'element' && deletedCollectionIds.includes(item.sourceCollectionId || ''))
                    )
                );
                saveToStorage(STORAGE_KEYS.TRASH, updatedTrash);
            }

            if (selectedCollectionId && deletedCollectionIds.includes(selectedCollectionId)) {
                setSelectedCollectionId(null);
            }

            return prevFolders.filter((folder) => folder.id !== folderId);
        });
    };

    const handleSelectCollection = (collectionId: string) => {
        setSelectedCollectionId(collectionId);
    };

    const handleRestoreBackup = (backup: AppBackup) => {
        const {
            folders: restoredFolders,
            selectedCollectionId: restoredCollectionId,
            trash,
            secretCollectionPin,
        } = backup.data;

        saveToStorage(STORAGE_KEYS.FOLDERS, restoredFolders);
        saveToStorage(STORAGE_KEYS.TRASH, trash);

        if (restoredCollectionId) {
            saveToStorage(STORAGE_KEYS.SELECTED_COLLECTION_ID, restoredCollectionId);
        } else {
            removeFromStorage(STORAGE_KEYS.SELECTED_COLLECTION_ID);
        }

        if (secretCollectionPin) {
            saveToStorage(STORAGE_KEYS.SECRET_COLLECTION_PIN, secretCollectionPin);
        } else {
            removeFromStorage(STORAGE_KEYS.SECRET_COLLECTION_PIN);
        }

        setFolders(restoredFolders);
        setSelectedCollectionId(restoredCollectionId);
    };

    const dismissBetaNotice = () => {
        setIsBetaNoticeDismissed(true);
    };

    const installApp = async () => {
        await requestInstall();
    };

    const hiddenSecretCollectionIds = useMemo(
        () => new Set(
            secretCollections.areSecretCollectionsVisible
                ? []
                : folders.flatMap((folder) =>
                    (folder.collections || [])
                        .filter((collection) => collection.secret)
                        .map((collection) => collection.id),
                ),
        ),
        [secretCollections.areSecretCollectionsVisible, folders],
    );
    const visibleSelectedCollectionId = hiddenSecretCollectionIds.has(selectedCollectionId || '')
        ? null
        : selectedCollectionId;

    useEffect(() => {
        if (selectedCollectionId && hiddenSecretCollectionIds.has(selectedCollectionId)) {
            setSelectedCollectionId(null);
        }
    }, [hiddenSecretCollectionIds, selectedCollectionId]);

    const plannerFolders = useMemo(
        () => folders.map((folder) => ({
            ...folder,
            collections: (folder.collections || []).filter(
                (collection) => !hiddenSecretCollectionIds.has(collection.id),
            ),
        })),
        [folders, hiddenSecretCollectionIds],
    );

    const mergePlannerFolders = (updatedFolders: Folder[]) => {
        setFolders((currentFolders) => currentFolders.map((folder) => {
            const updatedFolder = updatedFolders.find((candidate) => candidate.id === folder.id);
            if (!updatedFolder) return folder;

            const hiddenCollections = (folder.collections || []).filter((collection) =>
                hiddenSecretCollectionIds.has(collection.id),
            );
            return { ...updatedFolder, collections: [...(updatedFolder.collections || []), ...hiddenCollections] };
        }));
    };

    const handleOpenTaskInCollection = ({ collectionId }: PlannerTaskInfo) => {
        setSelectedCollectionId(collectionId);
        navigate('/');
    };

    const handleAddSharedCollection = (collection: Collection) => {
        const sharedName = `${collection.name} (${t('shared.shared')})`;
        const newCollection: Collection = {
            id: generateId(), 
            name: sharedName, 
            elements: collection.elements || [],
            visibleTypes: ['task'] // Default to tasks only
        };
        // Add to the "General" folder (first folder)
        setFolders((prevFolders) => {
            if (prevFolders.length === 0) {
                return [{ id: generateId(), name: t('general.general'), collections: [newCollection] }];
            }
            return prevFolders.map((folder, index) =>
                index === 0
                    ? { ...folder, collections: [...(folder.collections || []), newCollection] }
                    : folder
            );
        });
        setSelectedCollectionId(newCollection.id);
    };

    const handleRestoreElement = (element: Element, sourceCollectionId: string) => {
        setFolders((prevFolders) =>
            prevFolders.map((folder) => ({
                ...folder,
                collections: (folder.collections || []).map((collection) =>
                    collection.id === sourceCollectionId
                        ? { ...collection, elements: [...(collection.elements || []), element] }
                        : collection,
                ),
            })),
        );
        setSelectedCollectionId(sourceCollectionId);
    };

    const handleRestoreCollection = (collection: Collection, sourceFolderId: string) => {
        setFolders((prevFolders) =>
            prevFolders.map((folder) =>
                folder.id === sourceFolderId
                    ? { ...folder, collections: [...(folder.collections || []), collection] }
                    : folder,
            ),
        );
        setSelectedCollectionId(collection.id);
    };

    const handleRestoreFolder = (folder: Folder) => {
        setFolders((prevFolders) => [...prevFolders, folder]);
    };

    useEffect(() => {
        const cleanedFolders = sanitizeFolders(folders);
        if (cleanedFolders !== folders) {
            setFolders(cleanedFolders);
            return;
        }

        saveToStorage(STORAGE_KEYS.FOLDERS, folders);

        if (!selectedCollectionId) {
            const firstCollectionId = getFirstCollectionId(
                folders.map((folder) => ({
                    ...folder,
                    collections: (folder.collections || []).filter(
                        (collection) => !hiddenSecretCollectionIds.has(collection.id),
                    ),
                })),
            );
            if (firstCollectionId) {
                setSelectedCollectionId(firstCollectionId);
            }
        }
    }, [folders, hiddenSecretCollectionIds, selectedCollectionId]);

    // Extract all tasks from folders for reminders
    const allTasks = useMemo(() => {
        const tasks: Task[] = [];
        folders.forEach((folder) => {
            (folder.collections || []).forEach((collection) => {
                if (hiddenSecretCollectionIds.has(collection.id)) return;
                (collection.elements || []).forEach((element) => {
                    if (element.type === 'task') {
                        const task = element as Task;
                        tasks.push(task);
                        // Also include subtasks
                        task.subtasks?.forEach((subtask) => {
                            tasks.push(subtask);
                        });
                    }
                });
            });
        });
        return tasks;
    }, [folders, hiddenSecretCollectionIds]);

    // Helper function to find and update a task by ID
    const clearReminderFromTask = (taskId: string) => {
        const updateElement = (element: Element): Element => {
            if (element.type !== 'task') return element;
            const task = element as Task;

            if (task.id === taskId) {
                return { ...task, reminderTime: undefined };
            }

            if (task.subtasks) {
                const updatedSubtasks = task.subtasks.map((subtask) =>
                    subtask.id === taskId ? { ...subtask, reminderTime: undefined } : subtask
                );
                const hasChanges = updatedSubtasks.some((st, i) => st.id !== task.subtasks?.[i].id);
                if (hasChanges) {
                    return { ...task, subtasks: updatedSubtasks };
                }
            }

            return task;
        };

        setFolders((prevFolders) =>
            prevFolders.map((folder) => ({
                ...folder,
                collections: (folder.collections || []).map((collection) => ({
                    ...collection,
                    elements: (collection.elements || []).map(updateElement),
                })),
            }))
        );
    };

    // Use reminders hook
    useReminders(allTasks, clearReminderFromTask);

    return (
        <>
            <NavigationMenu
                folders={folders}
                setFolders={setFolders}
                selectedCollectionId={visibleSelectedCollectionId}
                onAddFolder={handleAddFolder}
                onSelectCollection={handleSelectCollection}
                onMoveCollectionToFolder={handleMoveCollectionToFolder}
                onDeleteFolder={handleDeleteFolder}
                onRestoreElement={handleRestoreElement}
                onRestoreCollection={handleRestoreCollection}
                onRestoreFolder={handleRestoreFolder}
                areSecretCollectionsVisible={secretCollections.areSecretCollectionsVisible}
                onRequestSecretCollectionsVisibility={secretCollections.requestSecretCollectionsVisibility}
                onRequestSetPin={secretCollections.requestSetPin}
                onRequestChangePin={secretCollections.requestChangePin}
                onRequestDeletePin={secretCollections.requestDeletePin}
                onRequestSecretCollectionCreation={secretCollections.requestSecretCollectionCreation}
                onRestoreBackup={handleRestoreBackup}
                isInstallAvailable={isInstallAvailable}
                onInstallApp={installApp}
            />
            {shouldShowInstallBanner && (
                <aside className="app-install-banner">
                    {shouldShowIosInstallGuide ? (
                        <span className="app-ios-install-guide">
                            <FontAwesomeIcon icon={faArrowUpFromBracket} />
                            {t('pwa.iosInstallGuide')}
                        </span>
                    ) : (
                        <button type="button" className="app-install-button" onClick={installApp}>
                            <FontAwesomeIcon icon={faDownload} />
                            {t('pwa.install')}
                        </button>
                    )}
                    <button
                        type="button"
                        className="app-install-banner-close"
                        onClick={() => setIsInstallBannerDismissed(true)}
                        aria-label={t('pwa.dismissInstall')}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </aside>
            )}
            <Routes>
                {/* Default route for your current layout */}
                <Route
                    path="/"
                    element={
                        <Dashboard
                            selectedCollectionId={visibleSelectedCollectionId || undefined}
                            onDeleteCollection={handleDeleteCollection}
                            folders={folders}
                            setFolders={setFolders}
                            hiddenCollectionIds={hiddenSecretCollectionIds}
                            onChangeCollectionSecretStatus={secretCollections.requestCollectionSecretStatusChange}
                        />
                    }
                />

                {/* Route for daily planner */}
                <Route
                    path="/planner"
                    element={
                        <div className="home-screen">
                            <DailyPlanner
                                folders={plannerFolders}
                                setFolders={mergePlannerFolders}
                                onOpenTaskInCollection={handleOpenTaskInCollection}
                            />
                        </div>
                    }
                />

                {/* Route for shared collection */}
                <Route
                    path="/share"
                    element={
                        <SharedCollectionView
                            setSharedCollection={handleAddSharedCollection}
                        />
                    }
                />
            </Routes>
            {!isBetaNoticeDismissed && (
                <footer className="app-beta-notice">
                    <button
                        type="button"
                        className="app-beta-notice-info"
                        onClick={() => setIsBetaInfoOpen(true)}
                        aria-label={t('appStatus.moreInfo')}
                    >
                        <FontAwesomeIcon icon={faCircleInfo} />
                    </button>
                    <span>{t('appStatus.localDataNotice')}</span>
                    <button
                        type="button"
                        className="app-beta-notice-close"
                        onClick={dismissBetaNotice}
                        aria-label={t('appStatus.dismissNotice')}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </footer>
            )}
            {isBetaInfoOpen && (
                <div className="app-info-overlay" onClick={() => setIsBetaInfoOpen(false)}>
                    <section
                        className="app-info-dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="app-beta-info-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="app-info-dialog-header">
                            <h2 id="app-beta-info-title">{t('appStatus.infoTitle')}</h2>
                            <button
                                type="button"
                                className="app-info-dialog-close"
                                onClick={() => setIsBetaInfoOpen(false)}
                                aria-label={t('appStatus.closeInfo')}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                        <p>{t('appStatus.infoMessage')}</p>
                    </section>
                </div>
            )}
            {secretCollections.pinModal && (
                <SecretPinModal
                    mode={secretCollections.pinModal.mode}
                    purpose={secretCollections.pinModal.purpose}
                    onSubmit={secretCollections.handlePinSubmit}
                    onCancel={secretCollections.cancelPinModal}
                />
            )}
            {secretCollections.showDeletePinConfirmation && (
                <ConfirmationDialog
                    title={t('secretCollections.deletePinConfirmation.title')}
                    message={t('secretCollections.deletePinConfirmation.message')}
                    onConfirm={secretCollections.confirmDeletePin}
                    onCancel={secretCollections.cancelDeletePinConfirmation}
                    isDanger
                />
            )}
        </>
    );
}

export default App;
